/**
 * Inventory Validation API Module
 * Validates product/variant availability via backend API on PLP/PDP page load
 */
(function() {
  'use strict';

  const InventoryValidation = {
    config: {
      baseUrl: FILA_API_BASE_URL + '/api/v3/products/available-for-sale',
      cacheTimeout: 5 * 60 * 1000, // 5 minutes cache
      requestTimeout: 5000, // 5 seconds timeout
    },

    cache: new Map(),
    isInitialized: false,

    /**
     * Collect all product IDs from the current page
     * @returns {string[]} Array of product IDs
     */
    collectProductIds() {
      const productIds = new Set();

      // PLP: Collect from product grid items
      document.querySelectorAll('[data-product-id]').forEach(el => {
        const id = el.getAttribute('data-product-id');
        if (id) productIds.add(id);
      });

      // PDP: Collect from product-info element
      const productInfo = document.querySelector('product-info[data-product-id]');
      if (productInfo) {
        const id = productInfo.getAttribute('data-product-id');
        if (id) productIds.add(id);
      }

      // Also collect from data-product-main-block-id
      document.querySelectorAll('[data-product-main-block-id]').forEach(el => {
        const id = el.getAttribute('data-product-main-block-id');
        if (id) productIds.add(id);
      });

      return Array.from(productIds);
    },

    /**
     * Call the inventory validation API
     * @param {string[]} productIds
     * @returns {Promise<Object>}
     */
    async fetchAvailability(productIds) {
      if (!productIds.length) return { success: true, data: [] };

      // Check cache
      const cacheKey = productIds.sort().join(',');
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.config.cacheTimeout) {
        return cached.data;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.requestTimeout);

      try {
        const response = await fetch(this.config.baseUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ productIds }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();

        // Cache the result
        this.cache.set(cacheKey, {
          timestamp: Date.now(),
          data,
        });

        return data;
      } catch (error) {
        clearTimeout(timeoutId);

        if (error.name === 'AbortError') {
          console.warn('Inventory validation API timeout, using Shopify availability');
        } else {
          console.error('Inventory validation API error:', error);
        }

        // Fallback: Keep existing Shopify availability
        return { success: false, data: [], fallback: true };
      }
    },

    /**
     * Update DOM based on API response
     * @param {Object} apiResponse
     */
    updateAvailabilityUI(apiResponse) {
      if (!apiResponse.success || !apiResponse.data) {
        return;
      }

      // Build global variant availability map from all products in API response
      const variantMap = new Map();
      apiResponse.data.forEach(product => {
        if (product.variants) {
          product.variants.forEach(v => {
            variantMap.set(String(v.variantId), v.availableForSale);
          });
        }
      });

      apiResponse.data.forEach(product => {
        const productId = product.productId;

        // Find product containers (PLP cards, PDP)
        const productContainers = document.querySelectorAll(
          `[data-product-id="${productId}"], [data-product-main-block-id="${productId}"]`
        );

        productContainers.forEach(container => {
          this.updateProductContainer(container, product, variantMap);
        });
      });
    },

    /**
     * Update a single product container with variant availability
     * @param {Element} container
     * @param {Object} productData
     * @param {Map} variantMap - Global variant availability map (variantId -> availableForSale)
     */
    updateProductContainer(container, productData, variantMap) {
      // Find the card wrapper inside the container
      const cardWrapper = container.matches('product-info[data-product-id]')
        ? container
        : container.querySelector('[data-product-card]') || container;

      // Update the embedded variant data script
      const variantDataScript = cardWrapper.querySelector('script[card-data-variants]');
      let updatedVariants = [];

      if (variantDataScript) {
        try {
          const variants = JSON.parse(variantDataScript.textContent);
          updatedVariants = variants.map(variant => {
            // First check product-specific API data
            const apiVariant = productData.variants.find(
              v => String(v.variantId) === String(variant.id)
            );
            if (apiVariant) {
              return {
                ...variant,
                available: apiVariant.availableForSale,
                api_validated: true,
              };
            }
            // Fallback: check global variant map (covers cross-product/bundle variants)
            if (variantMap) {
              const globalAvailable = variantMap.get(String(variant.id));
              if (globalAvailable !== undefined) {
                return {
                  ...variant,
                  available: globalAvailable,
                  api_validated: true,
                };
              }
            }
            return variant;
          });
          variantDataScript.textContent = JSON.stringify(updatedVariants);
        } catch (e) {
          console.error('Error updating variant data:', e);
        }
      }

      // Update size option items with data-variant-id
      productData.variants.forEach(variant => {
        const variantId = variant.variantId;
        const isAvailable = variant.availableForSale;

        // Update option items with variant-id (PLP cards)
        const optionItems = cardWrapper.querySelectorAll(
          `[data-variant-id="${variantId}"]`
        );

        optionItems.forEach(item => {
          // PLP/PDP: card__content-option-item class
          if (item.classList.contains('card__content-option-item')) {
            // 사이즈 텍스트 추출 (span에서 또는 직접 텍스트에서)
            const existingSizeSpan = item.querySelector('.card__content-option-item-size');
            const sizeText = existingSizeSpan
              ? existingSizeSpan.textContent.trim()
              : item.getAttribute('data-option-value') || item.textContent.trim();

            if (isAvailable) {
              item.classList.remove('card__content-option-item--unavailable');
              item.classList.remove('restock-button');
              // 재고가 있으면 Out of stock 텍스트 제거하고 사이즈만 표시
              const stockSpan = item.querySelector('.card__content-option-item-stock');
              if (stockSpan) stockSpan.remove();
              if (existingSizeSpan) {
                existingSizeSpan.remove();
              }
              item.textContent = sizeText;
            } else {
              item.classList.add('card__content-option-item--unavailable');
              item.classList.add('restock-button');
              const cardPid = cardWrapper.getAttribute('data-product-id');
              if (cardPid) item.setAttribute('data-product-id', cardPid);
              // 입고알림 문구가 없으면 추가
              if (!item.querySelector('.card__content-option-item-stock')) {
                item.innerHTML = '';

                const sizeSpan = document.createElement('span');
                sizeSpan.className = 'card__content-option-item-size';
                sizeSpan.textContent = sizeText;
                item.appendChild(sizeSpan);

                const stockSpan = document.createElement('span');
                stockSpan.className = 'card__content-option-item-stock';
                stockSpan.innerHTML = '입고알림 신청 <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="card__content-option-item--stock-bell"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>';
                item.appendChild(stockSpan);
              }
            }
          }
          // PDP: input[type="radio"] with disabled class
          else if (item.tagName === 'INPUT' && item.type === 'radio') {
            const label = cardWrapper.querySelector(`label[for="${item.id}"]`);
            if (isAvailable) {
              item.classList.remove('disabled');
              if (label) {
                label.classList.remove('restock-button');
                // Remove bell icon if exists
                const bellIcon = label.querySelector('.restock-bell-icon, svg, .icon');
                if (bellIcon) bellIcon.remove();
              }
            } else {
              item.classList.add('disabled');
              if (label) {
                label.classList.add('restock-button');
                label.setAttribute('data-product-id', productData.productId);
                label.setAttribute('data-size-value', item.value);
                let sizeText = label.querySelector('.restock-label-text');
                if (!sizeText) {
                  const unavailableText = label.querySelector('.label-unavailable');
                  sizeText = document.createElement('span');
                  sizeText.className = 'restock-label-text';
                  sizeText.textContent = item.value;
                  label.replaceChildren(sizeText);
                  if (unavailableText) label.prepend(unavailableText);
                }
                if (!sizeText.querySelector('.restock-bell-icon')) {
                  sizeText.insertAdjacentHTML('beforeend', '<span class="restock-bell-icon" aria-hidden="true"><svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.60352 1.55884V2.89217" stroke="#939393" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.28841 3.32355C6.08174 3.32355 4.28841 5.11688 4.28841 7.32355V11.3235C3.62174 11.3235 2.95508 11.9902 2.95508 12.6569H8.28841M8.28841 3.32355C10.4951 3.32355 12.2884 5.11688 12.2884 7.32355V11.3235C12.9551 11.3235 13.6217 11.9902 13.6217 12.6569H8.28841" stroke="#939393" stroke-linecap="round" stroke-linejoin="round"/><path d="M7.26758 12.8922C7.26758 13.6255 7.86758 14.2255 8.60091 14.2255C9.33424 14.2255 9.93424 13.6255 9.93424 12.8922" stroke="#939393" stroke-linecap="round" stroke-linejoin="round"/></svg></span>');
                }
              }
            }
          }
        });

        // Update quick add buttons
        const quickAddBtns = cardWrapper.querySelectorAll(
          `.quick-add__submit[data-variant-id="${variantId}"]`
        );

        // QUICK ADD 텍스트는 유지, 사이즈 옵션에서만 품절 처리
      });

      // Update swatch items availability based on API response
      this.updateSwatchAvailability(cardWrapper, updatedVariants);

      // Check if all variants are unavailable
      // Only apply sold-out overlay to actual product cards, not PDP section containers
      // (PDP section has data-product-id but no [data-product-card], and querySelector('.card__media')
      //  would incorrectly match buy-together cards inside the section)
      const allUnavailable = productData.variants.length > 0
        && productData.variants.every(v => !v.availableForSale);
      const isRestock = cardWrapper.getAttribute('data-display-status') === '입고예정';

      this.updatePdpSubmitButton(cardWrapper, productData.productId, allUnavailable);

      // 전체 품절 + 입고예정이 아닌 경우 → SOLD OUT 오버레이 적용
      if (allUnavailable && cardWrapper.hasAttribute('data-product-card') && !isRestock) {
        cardWrapper.setAttribute('data-display-status-sold-out', 'true');
        const cardMedia = cardWrapper.querySelector('.card__media');
        if (cardMedia) {
          cardMedia.classList.add('card__media--sold-out');
        }
      }

      // 입고예정 + 전체 품절 → QUICK ADD 텍스트를 '입고예정'으로 교체, 사이즈 옵션 숨김
      if (isRestock && allUnavailable && cardWrapper.hasAttribute('data-product-card')) {
        const btnText = cardWrapper.querySelector('[data-quick-add-text]');
        if (btnText) {
          btnText.innerHTML = btnText.innerHTML.replace('QUICK ADD', '입고예정');
          btnText.classList.remove('hidden');
        }
        const sizeHeading = cardWrapper.querySelector('.card__content-option-list-heading');
        const sizeList = cardWrapper.querySelector('.card__content-option-list');
        if (sizeHeading) sizeHeading.style.display = 'none';
        if (sizeList) sizeList.style.display = 'none';
      }
    },

    updatePdpSubmitButton(container, productId, allUnavailable) {
      const productInfo = container.matches?.('product-info[data-product-id]')
        ? container
        : container.closest?.('product-info[data-product-id]');

      if (!productInfo || productInfo.getAttribute('data-product-id') !== String(productId)) return;

      const submitButton = productInfo.querySelector('product-form [data-atc-btn]');
      const buttonText = submitButton?.querySelector('[data-btn-text]');
      if (!submitButton || !buttonText) return;

      if (allUnavailable) {
        const soldOutText = window.variantStrings && window.variantStrings.soldOut;
        if (soldOutText) buttonText.textContent = soldOutText;

        submitButton.setAttribute('data-inventory-all-unavailable', 'true');
        submitButton.setAttribute('data-no-variant-selected', '');
        if (!submitButton.hasAttribute('data-inventory-validating')) {
          submitButton.removeAttribute('disabled');
        }

        const variantInput = productInfo.querySelector('product-form input[name="id"]');
        if (variantInput) {
          variantInput.value = '';
          variantInput.disabled = true;
        }
        return;
      }

      if (submitButton.hasAttribute('data-inventory-all-unavailable')) {
        submitButton.removeAttribute('data-inventory-all-unavailable');
        buttonText.textContent = window.variantStrings.addToCart;

        const variantInput = productInfo.querySelector('product-form input[name="id"]');
        if (variantInput) {
          variantInput.disabled = false;
        }

        if (!submitButton.hasAttribute('data-inventory-validating')) {
          submitButton.removeAttribute('disabled');
        }
      }
    },

    /**
     * Update swatch item availability based on variant data
     * @param {Element} cardWrapper
     * @param {Array} variants - Updated variant data with api_validated flag
     */
    updateSwatchAvailability(cardWrapper, variants) {
      if (!variants || variants.length === 0) return;

      const swatchItems = cardWrapper.querySelectorAll('[data-product-swatch]');
      if (swatchItems.length === 0) return;

      swatchItems.forEach(swatch => {
        const swatchColor = swatch.getAttribute('data-value');
        const swatchProductUrl = (swatch.getAttribute('data-product-url') || '').split('?')[0];
        if (!swatchColor) return;

        // data-display-status가 '품절' 또는 '입고예정'인 스와치는 API 검증으로 변경하지 않음
        const swatchDisplayStatus = swatch.getAttribute('data-display-status');
        if (swatchDisplayStatus === '품절' || swatchDisplayStatus === '입고예정') return;

        const swatchColorLower = swatchColor.toLowerCase().trim();

        // Find all variants matching this swatch color
        const matchingVariants = variants.filter(variant => {
          const variantProductUrl = (variant.product_url || '').split('?')[0];
          if (swatchProductUrl && variantProductUrl) {
            return variantProductUrl === swatchProductUrl;
          }
          if (!variant.title) return false;
          // Extract color from variant title (format: "color, size")
          const lastCommaIndex = variant.title.lastIndexOf(',');
          if (lastCommaIndex === -1) return false;
          const colorPart = variant.title.substring(0, lastCommaIndex).trim().toLowerCase();
          return colorPart === swatchColorLower;
        });

        // If no matching variants found, skip this swatch
        if (matchingVariants.length === 0) return;

        // Check if all matching variants are unavailable (considering api_validated and is_sold_out)
        const allColorVariantsUnavailable = matchingVariants.every(variant => {
          // Use api_validated value if available, otherwise use original available value
          const isAvailable = variant.api_validated ? variant.available : (variant.available === true);
          // Also check is_sold_out flag
          const isSoldOut = variant.is_sold_out === true;
          return !isAvailable || isSoldOut;
        });

        if (allColorVariantsUnavailable) {
          swatch.classList.add('card__content-swatch-item--unavailable');
        } else {
          swatch.classList.remove('card__content-swatch-item--unavailable');
        }
      });

      // Update sold-out state based on active swatch (only for product cards, not PDP sections)
      if (cardWrapper.hasAttribute('data-product-card')) {
        const activeSwatch = cardWrapper.querySelector('[data-product-swatch].card__content-swatch-item--active');
        if (activeSwatch) {
          const isActiveSwatchUnavailable = activeSwatch.classList.contains('card__content-swatch-item--unavailable');
          const mediaDiv = cardWrapper.querySelector('.card__media');
          const activeSwatchDisplayStatus = activeSwatch.getAttribute('data-display-status');

          if (isActiveSwatchUnavailable && activeSwatchDisplayStatus !== '입고예정') {
            cardWrapper.setAttribute('data-display-status-sold-out', 'true');
            if (mediaDiv) {
              mediaDiv.classList.add('card__media--sold-out');
            }
          } else {
            // Only remove sold-out state if it was set by API validation, not by metafield
            const originalDisplayStatus = cardWrapper.getAttribute('data-display-status');
            if (originalDisplayStatus !== '품절') {
              cardWrapper.removeAttribute('data-display-status-sold-out');
              if (mediaDiv) {
                mediaDiv.classList.remove('card__media--sold-out');
              }
            }
          }
        }
      }
    },

    /**
     * Show loading state
     */
    showLoading() {
      document.querySelectorAll('[data-product-card]').forEach(card => {
        card.classList.add('inventory-validating');
      });
    },

    /**
     * Hide loading state
     */
    hideLoading() {
      document.querySelectorAll('[data-product-card]').forEach(card => {
        card.classList.remove('inventory-validating');
      });
    },

    setPdpSubmitLoading(isLoading) {
      const submitButton = document.querySelector(
        'product-info[data-product-id] product-form [data-atc-btn]'
      );
      if (!submitButton) return;

      if (isLoading) {
        if (submitButton.hasAttribute('data-inventory-validating')) return;

        submitButton.setAttribute(
          'data-inventory-was-disabled',
          submitButton.hasAttribute('disabled') ? 'true' : 'false'
        );
        submitButton.setAttribute('data-inventory-validating', 'true');
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.setAttribute('disabled', '');
        return;
      }

      const wasDisabled = submitButton.getAttribute('data-inventory-was-disabled') === 'true';
      submitButton.removeAttribute('data-inventory-validating');
      submitButton.removeAttribute('data-inventory-was-disabled');
      submitButton.removeAttribute('aria-busy');

      if (submitButton.hasAttribute('data-inventory-all-unavailable')) {
        submitButton.removeAttribute('disabled');
      } else if (wasDisabled) {
        submitButton.setAttribute('disabled', '');
      } else {
        submitButton.removeAttribute('disabled');
      }
    },

    /**
     * Initialize and run validation
     */
    async init() {
      const productInfo = document.querySelector('product-info.main-product[data-update-url="true"]');
      const isCurrentProduct = () => productInfo === document.querySelector('product-info.main-product[data-update-url="true"]');
      const productIds = this.collectProductIds();
      if (productIds.length === 0) return;

      this.showLoading();
      this.setPdpSubmitLoading(true);

      try {
        const response = await this.fetchAvailability(productIds);
        if (!isCurrentProduct()) return;
        this.updateAvailabilityUI(response);

        // Dispatch custom event for other components to listen
        document.dispatchEvent(new CustomEvent('inventory:validated', {
          detail: response,
        }));
      } catch (error) {
        console.error('Inventory validation failed:', error);
      } finally {
        if (isCurrentProduct()) {
          this.setPdpSubmitLoading(false);
          this.hideLoading();
        }
      }
    },

    /**
     * Setup event listeners for dynamic content
     */
    setup() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      // Initialize on DOMContentLoaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }

      document.addEventListener('product-info:loaded', ({ target }) => {
        if (document.readyState === 'loading') return;
        if (!target.matches?.('product-info.main-product[data-update-url="true"]')) return;
        this.init();
      });

      // Re-validate when products are dynamically loaded (pagination, filters)
      document.addEventListener('facets:render', () => {
        // Clear cache for fresh validation after filter changes
        this.cache.clear();
        setTimeout(() => this.init(), 100);
      });

      // Re-validate when collection is reloaded (custom sort)
      document.addEventListener('collection:reloaded', () => {
        setTimeout(() => this.init(), 100);
      });

      // Observe for dynamically added products (infinite scroll)
      this.setupMutationObserver();
    },

    /**
     * Setup mutation observer for infinite scroll
     */
    setupMutationObserver() {
      const productGrid = document.getElementById('product-grid');
      if (!productGrid) return;

      const observer = new MutationObserver((mutations) => {
        const hasNewProducts = mutations.some(mutation =>
          Array.from(mutation.addedNodes).some(node =>
            node.nodeType === 1 && (
              node.matches?.('[data-product-card]') ||
              node.querySelector?.('[data-product-card]')
            )
          )
        );

        if (hasNewProducts) {
          // Debounce the init call
          clearTimeout(this.mutationTimeout);
          this.mutationTimeout = setTimeout(() => this.init(), 200);
        }
      });

      observer.observe(productGrid, {
        childList: true,
        subtree: true,
      });
    },
  };

  // Initialize
  InventoryValidation.setup();

  // Expose globally for debugging and other modules
  window.InventoryValidation = InventoryValidation;
})();
